import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { isAllowedExternalRedirect, isPartnerPortalHandoff } from './external-redirect';

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  method: 'direct' | 'social' | null;
}

interface UserProfile {
  name?: string;
  nickname?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PortalAuthService {
  private http = inject(HttpClient);
  private auth0 = inject(AuthService);
  private router = inject(Router);

  private authState$ = new BehaviorSubject<AuthState>({
    token: null,
    isAuthenticated: false,
    method: null
  });

  private _userProfile$ = new BehaviorSubject<UserProfile | null>(null);
  public readonly userProfile$ = this._userProfile$.asObservable();

  public readonly isAuthenticated$ = this.authState$.pipe(
    map(state => state.isAuthenticated && !!state.token && !this.isTokenExpired(state.token))
  );

  constructor() {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    const token = localStorage.getItem('authToken');
    if (token && !this.isTokenExpired(token)) {
      this.authState$.next({
        token,
        isAuthenticated: true,
        method: localStorage.getItem('authMethod') as 'direct' | 'social' | null
      });
    }

    // Restore user profile from localStorage
    const storedProfile = localStorage.getItem('userProfile');
    if (storedProfile) {
      try {
        this._userProfile$.next(JSON.parse(storedProfile));
      } catch { /* ignore */ }
    }
  }

  // Direct login with email/password
  loginDirect(email: string, password: string): Observable<{ access_token: string; refresh_token?: string }> {
    const body = {
      grant_type: 'password',
      username: email,
      password: password,
      audience: environment.auth0.audience,
      scope: environment.auth0.scope,
      client_id: environment.auth0.clientId,
      connection: 'Username-Password-Authentication'
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<{ access_token: string; refresh_token?: string }>(`https://${environment.auth0.domain}/oauth/token`, body, { headers }).pipe(
      tap(response => {
        this.storeAuth(response.access_token, 'direct', response.refresh_token);
        // Fire-and-forget: fetch user profile from /userinfo (JWT doesn't include name).
        // This runs as an independent subscription so it survives component destruction.
        this.fetchUserProfile(response.access_token).subscribe();
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  // Social login with redirect
  loginWithProvider(provider: string): void {
    localStorage.setItem('authMethod', 'social');
    this.auth0.loginWithRedirect({
      authorizationParams: {
        connection: provider
      }
    });
  }

  // Federated handoff login: used when an external app (VI) launches us with a
  // `?source&returnUrl` handoff and no session exists. Redirects to Azure AD via
  // the enterprise connection (no prompt=none), which returns silently when the
  // user already has an AAD session (e.g. launched from VerticalInSite) —
  // establishing the Auth0 session with no visible login. Only when there is no
  // AAD session does the Microsoft sign-in page appear. prompt=none is not used:
  // it requires an existing Auth0 session and cannot bootstrap one from the
  // upstream AAD session. Callers gate this to VB users, since it always targets
  // the Azure AD connection.
  federatedLoginForHandoff(loginHint: string | null): void {
    localStorage.setItem('authMethod', 'social');
    const authorizationParams: Record<string, string> = {
      connection: environment.auth0.connection,
    };
    if (loginHint) {
      authorizationParams['login_hint'] = loginHint;
    }
    this.auth0.loginWithRedirect({ authorizationParams });
  }

  // Request password reset via Auth0
  requestPasswordReset(email: string): Observable<string> {
    const body = {
      client_id: environment.auth0.clientId,
      email: email,
      connection: 'Username-Password-Authentication'
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<string>(`https://${environment.auth0.domain}/dbconnections/change_password`, body, { headers, responseType: 'text' as 'json' });
  }

  // Sign up with email/password using Auth0 Database API
  signUpDirect(email: string, password: string, names?: { name?: string; givenName?: string; familyName?: string }): Observable<{ _id: string; email: string }> {
    const body: {
      client_id: string;
      email: string;
      password: string;
      connection: string;
      name?: string;
      given_name?: string;
      family_name?: string;
    } = {
      client_id: environment.auth0.clientId,
      email: email,
      password: password,
      connection: 'Username-Password-Authentication'
    };

    if (names?.name) {
      body.name = names.name;
    }
    if (names?.givenName) {
      body.given_name = names.givenName;
    }
    if (names?.familyName) {
      body.family_name = names.familyName;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<{ _id: string; email: string }>(`https://${environment.auth0.domain}/dbconnections/signup`, body, { headers });
  }

  // Handle callback from Auth0
  handleCallback(): Observable<boolean> {
    return this.auth0.isAuthenticated$.pipe(
      switchMap(isAuth => {
        if (isAuth) {
          return this.auth0.getAccessTokenSilently().pipe(
            tap(token => {
              this.storeAuth(token, 'social');
            }),
            map(() => true),
            catchError(() => of(false))
          );
        }
        return of(false);
      })
    );
  }

  // Store authentication data
  private storeAuth(token: string, method: 'direct' | 'social', refreshToken?: string): void {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authMethod', method);

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    this.authState$.next({
      token,
      isAuthenticated: true,
      method
    });
  }

  // Get current token
  getToken(): string | null {
    const token = this.authState$.value.token;
    if (token && !this.isTokenExpired(token)) {
      return token;
    }
    return null;
  }

  // Check if token is expired
  private isTokenExpired(token: string): boolean {
    if (!token) return true;

    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const exp = decoded.exp;

      if (!exp) return false;
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  }

  // Refresh token
  refreshToken(): Observable<string | null> {
    const method = this.authState$.value.method;

    if (method === 'direct') {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return of(null);

      const body = {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: environment.auth0.clientId
      };

      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

      return this.http.post<{ access_token: string; refresh_token?: string }>(`https://${environment.auth0.domain}/oauth/token`, body, { headers }).pipe(
        tap(response => {
          this.storeAuth(response.access_token, 'direct', response.refresh_token);
        }),
        map(response => response.access_token),
        catchError(() => {
          // A failed refresh must not tear down a session that still holds a
          // valid token. This refresh is best-effort (e.g. pulling in freshly
          // assigned role claims after provisioning), not an auth gate. Only
          // force a logout when the session no longer has a usable token.
          if (!this.getToken()) {
            this.logout();
          }
          return of(null);
        })
      );
    } else {
      // Social auth - use Auth0 SDK
      return from(this.auth0.getAccessTokenSilently({ cacheMode: 'off' })).pipe(
        tap(token => {
          this.storeAuth(token, 'social');
        }),
        catchError(() => {
          // See note above: don't destroy a still-valid session just because a
          // best-effort silent refresh failed. Auth0 can legitimately fail this
          // (e.g. no refresh token issued for an enterprise/social connection).
          if (!this.getToken()) {
            this.logout();
          }
          return of(null);
        })
      );
    }
  }

  // Clear any existing auth state without redirecting (used before direct signup/login)
  clearAuthState(): void {
    // Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authMethod');
    localStorage.removeItem('externalRedirectUrl');
    localStorage.removeItem('externalRedirectSource');
    localStorage.removeItem('externalLoginHint');
    localStorage.removeItem('userProfile');

    // Reset state
    this.authState$.next({
      token: null,
      isAuthenticated: false,
      method: null
    });
    this._userProfile$.next(null);
  }

  // Logout
  logout(): void {
    this.clearAuthState();

    // Auth0 logout redirects to returnTo URL, which reloads the app at '/'.
    // Non-federated: clears Auth0 client state only. The upstream Azure AD
    // session is intentionally preserved so signing out of a VB site doesn't
    // also sign the user out of Teams / Outlook / other M365 apps on this
    // machine.
    this.auth0.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  }

  // Returns true if redirecting (caller should skip navigating to /home).
  // Only for a cross-app handoff: caller (OC / SL / VI / PP) sent us a
  // returnUrl; redirect there with the token. Applies to every user.
  // The non-VB Partner Portal auto-redirect is left to HomeComponent, which
  // waits for the backend role check: the token alone can still grant PP to
  // a user whose request is pending approval.
  handlePostLoginRedirect(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const externalRedirect = localStorage.getItem('externalRedirectUrl');
    if (externalRedirect) {
      // SECURITY: this is the line that hands out the access token, so the
      // origin is re-validated here rather than trusted from localStorage.
      // The capture-time gates (AppComponent, LoginComponent) can be bypassed
      // by anything that writes the key directly, and stored values predating
      // those gates are still in circulation.
      if (!isAllowedExternalRedirect(externalRedirect)) {
        console.warn(
          'PortalAuthService: dropping stored external redirect to non-allowlisted origin',
          externalRedirect
        );
        localStorage.removeItem('externalRedirectUrl');
        localStorage.removeItem('externalRedirectSource');
        localStorage.removeItem('externalLoginHint');
        return false;
      }

      // A Partner Portal handoff is left in place for HomeComponent, which
      // sends the user back only once the backend confirms the request is
      // approved. Honouring it here would return a pending user to PP's
      // pending page.
      const rawSource = localStorage.getItem('externalRedirectSource');
      if (isPartnerPortalHandoff(rawSource, externalRedirect)) {
        return false;
      }

      const externalSource = rawSource ?? 'unknown';
      // Loop guard: if we already redirected this tab to this source in the
      // last 10 s, render the dashboard instead of bouncing again. Protects
      // against the source app shipping the user back here with fresh
      // ?source=&returnUrl= params after consuming the token (e.g. PP's auth
      // interceptor calling redirectToLoginUrl on a stale 401), which would
      // otherwise loop until the renderer crashes ("Aw, Snap"). Mirrors the
      // same guard already in features/home/home.component.ts.
      const lastRedirectKey = `lastPortalRedirect:${externalSource}`;
      const lastRedirectAt = Number(sessionStorage.getItem(lastRedirectKey) ?? '0');
      if (lastRedirectAt > 0 && Date.now() - lastRedirectAt < 10_000) {
        localStorage.removeItem('externalRedirectUrl');
        localStorage.removeItem('externalRedirectSource');
        localStorage.removeItem('externalLoginHint');
        return false;
      }
      sessionStorage.setItem(lastRedirectKey, Date.now().toString());
      localStorage.removeItem('externalRedirectUrl');
      localStorage.removeItem('externalRedirectSource');
      localStorage.removeItem('externalLoginHint');
      const url = `${externalRedirect}${externalRedirect.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      window.location.href = url;
      return true;
    }

    return false;
  }

  // Check authentication status
  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$;
  }

  // Fetch user profile from Auth0's /userinfo endpoint (includes name, unlike JWT access token)
  private fetchUserProfile(token: string): Observable<UserProfile | null> {
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

    return this.http.get<UserProfile>(`https://${environment.auth0.domain}/userinfo`, { headers }).pipe(
      tap(profile => {
        if (profile) {
          const userProfile = {
            name: profile.name,
            nickname: profile.nickname,
            email: profile.email
          };
          localStorage.setItem('userProfile', JSON.stringify(userProfile));
          this._userProfile$.next(userProfile);
        }
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  // Get user info from JWT token + stored user profile
  getUserInfo(): { email?: string; name?: string; nickname?: string; sub?: string } | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));

      // Check stored user profile (from /userinfo endpoint) as fallback for missing JWT claims
      let profileData: { name?: string; nickname?: string; email?: string } = {};
      const storedProfile = localStorage.getItem('userProfile');
      if (storedProfile) {
        try { profileData = JSON.parse(storedProfile); } catch { /* ignore */ }
      }

      const info = {
        email: decoded.email || decoded['https://verticalbridge.com/email'] || profileData.email,
        name: decoded.name || decoded['https://verticalbridge.com/name'] || profileData.name,
        nickname: decoded.nickname || profileData.nickname,
        sub: decoded.sub
      };
      return info;
    } catch {
      return null;
    }
  }

  // Update auth state from stored token (called after social login callback)
  updateAuthStateFromStorage(): void {
    const token = localStorage.getItem('authToken');
    const method = localStorage.getItem('authMethod') as 'direct' | 'social' | null;

    if (token && !this.isTokenExpired(token)) {
      this.authState$.next({
        token,
        isAuthenticated: true,
        method
      });
    }
  }
}
